import React, { useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcSpark = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/>
  </svg>
)
const IcEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
)
const IcUsers = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const IcClock = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
)
const IcMinus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)
const IcRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
)
const IcUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
)
const IcAlert = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)
const IcDesktop = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
)
const IcMobile = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
)
const IcTablet = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
)

const DEVICE_ICONS = { Desktop: IcDesktop, Mobile: IcMobile, Tablet: IcTablet }
const RANGES = [
  { label: '7d',  value: '7d'  },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
]

// ── SVG area chart ────────────────────────────────────────────────────────────
function AreaChartSVG({ data, height = 200 }) {
  const W = 600
  const H = height
  const PAD = { t: 10, r: 0, b: 20, l: 36 }
  const plotW = W - PAD.l - PAD.r
  const plotH = H - PAD.t - PAD.b

  if (!data || data.length < 2) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
        No data for this period
      </div>
    )
  }

  const vals = data.map(d => d.views || d.count || 0)
  const max = Math.max(...vals, 1)
  const pts = vals.map((v, i) => ({
    x: PAD.l + (i / (vals.length - 1)) * plotW,
    y: PAD.t + plotH - (v / max) * plotH,
  }))
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ')
  const area = [
    ...pts.map(p => `${p.x},${p.y}`),
    `${PAD.l + plotW},${PAD.t + plotH}`,
    `${PAD.l},${PAD.t + plotH}`,
  ].join(' ')

  // X-axis labels (show ~6)
  const labelIdxs = vals.length > 6 ? [0, Math.floor(vals.length / 5), Math.floor(2 * vals.length / 5), Math.floor(3 * vals.length / 5), Math.floor(4 * vals.length / 5), vals.length - 1] : data.map((_, i) => i)
  const uniqueIdxs = [...new Set(labelIdxs)]

  return (
    <div className="area-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height, width: '100%' }}>
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#6c63ff" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#6c63ff" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map(t => {
          const y = PAD.t + plotH * (1 - t)
          return (
            <g key={t}>
              <line x1={PAD.l} y1={y} x2={PAD.l + plotW} y2={y} stroke="var(--border)" strokeWidth="1" />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize="9" fill="var(--text-faint)">
                {Math.round(max * t).toLocaleString()}
              </text>
            </g>
          )
        })}
        {/* Area fill */}
        <polygon points={area} fill="url(#areaFill)" />
        {/* Line */}
        <polyline points={polyline} fill="none" stroke="#6c63ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* X labels */}
        {uniqueIdxs.map(i => (
          <text key={i} x={pts[i]?.x || 0} y={H - 2} textAnchor="middle" fontSize="9" fill="var(--text-faint)">
            {data[i]?.name || ''}
          </text>
        ))}
      </svg>
    </div>
  )
}

// ── SVG donut for devices ─────────────────────────────────────────────────────
function DonutChart({ data }) {
  if (!data || data.length === 0) {
    return <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No device data</div>
  }
  const R = 42, r = 28, cx = 55, cy = 55
  const total = data.reduce((s, d) => s + d.value, 0) || 1
  let angle = -90

  const arcs = data.map(d => {
    const deg = (d.value / total) * 360
    const startAngle = angle
    angle += deg
    const start = polarToCart(cx, cy, R, startAngle)
    const end   = polarToCart(cx, cy, R, angle - 0.5)
    const s2    = polarToCart(cx, cy, r, startAngle)
    const e2    = polarToCart(cx, cy, r, angle - 0.5)
    const large = deg > 180 ? 1 : 0
    return {
      d: `M ${start.x} ${start.y} A ${R} ${R} 0 ${large} 1 ${end.x} ${end.y} L ${e2.x} ${e2.y} A ${r} ${r} 0 ${large} 0 ${s2.x} ${s2.y} Z`,
      color: d.color,
      name: d.name,
      value: d.value,
    }
  })

  return (
    <div className="donut-wrap">
      <svg width="110" height="110" viewBox="0 0 110 110" style={{ flexShrink: 0 }}>
        {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
      </svg>
      <div className="donut-legend">
        {data.map((d, i) => {
          const Icon = DEVICE_ICONS[d.name] || IcDesktop
          return (
            <div key={i} className="donut-legend-item">
              <span className="donut-legend-dot" style={{ background: d.color }} />
              <Icon />
              <span style={{ flex: 1, color: 'var(--text-muted)' }}>{d.name}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text)' }}>{d.value}%</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function polarToCart(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, delta, up, icon, loading }) {
  return (
    <div className="analytics-kpi">
      <div className="analytics-kpi-icon">{icon}</div>
      <div className="analytics-kpi-value">
        {loading ? <div style={{ width: 80, height: 28, background: 'var(--border)', borderRadius: 6 }} /> : (typeof value === 'number' ? value.toLocaleString() : (value ?? '—'))}
      </div>
      <div className="analytics-kpi-label">{label}</div>
      {delta != null && !loading && (
        <div className={`analytics-kpi-delta${up ? ' up' : ' down'}`}>{up ? '↑' : '↓'} {delta}</div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const ClientAnalytics = () => {
  const [range, setRange] = useState('30d')

  // Preserved API call
  const { data: raw, isLoading, isFetching, isError, refetch } = useQuery(
    ['client-analytics', range],
    () => apiService.get(`/analytics/overview?range=${range}`).then(r => r.data),
    { staleTime: 120000, keepPreviousData: true, retry: 2 }
  )

  const data = raw || {}
  const {
    totalPageViews = 0,
    uniqueVisitors  = 0,
    activityData    = [],
    topPages        = [],
    deviceTypes     = [],
    referrerData    = [],
  } = data

  // Hourly bars (fake from activity data if not present)
  const hourlyData = data.hourlyData || activityData.slice(-24).map(d => d.views || 0)

  // Colors for sources
  const srcColors = ['#6c63ff', '#5BC0EB', '#E8654F', '#1FB596', '#f59e0b']

  if (isError) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <IcAlert />
        <div style={{ marginTop: 10, fontSize: 14, color: 'var(--text-muted)' }}>Failed to load analytics data.</div>
        <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ marginTop: 14 }} onClick={refetch}>
          <IcRefresh /> Retry
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 40 }}>

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'var(--text)' }}>Analytics</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {isFetching && !isLoading ? 'Updating…' : 'Track your website performance and visitor insights'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="range-toggle">
            {RANGES.map(r => (
              <button key={r.value} className={`range-btn${range === r.value ? ' active' : ''}`} onClick={() => setRange(r.value)}>
                {r.label}
              </button>
            ))}
          </div>
          <button className="ds-btn ds-btn-ghost ds-btn-sm" onClick={refetch} disabled={isFetching}>
            <IcRefresh />
          </button>
          <button className="ds-btn ds-btn-ghost ds-btn-sm">
            <IcUpload /> Export
          </button>
        </div>
      </div>

      {/* Insight banner */}
      {totalPageViews > 0 && (
        <div className="client-hero" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IcSpark />
            </span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {uniqueVisitors.toLocaleString()} unique visitors this {range === '7d' ? 'week' : range === '30d' ? 'month' : 'quarter'}
              </div>
              <div style={{ fontSize: 13, opacity: .8, marginTop: 4 }}>
                {totalPageViews.toLocaleString()} total page views recorded in this period.
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', right: -50, top: -50, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,.06)', pointerEvents: 'none', filter: 'blur(40px)' }} />
        </div>
      )}

      {/* KPI row */}
      <div className="analytics-kpi-grid">
        <KpiCard label="Page Views"      value={totalPageViews} icon={<IcEye />}   loading={isLoading} />
        <KpiCard label="Unique Visitors" value={uniqueVisitors}  icon={<IcUsers />} loading={isLoading} />
        <KpiCard label="Avg Time on Site" value={data.avgTime || '—'}  icon={<IcClock />} loading={isLoading} />
        <KpiCard label="Bounce Rate"     value={data.bounceRate ? `${data.bounceRate}%` : '—'} icon={<IcMinus />} loading={isLoading} />
      </div>

      {/* Main chart */}
      <div className="card card-pad" style={{ opacity: isFetching && !isLoading ? .7 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Visitors over time</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Last {range}</div>
          </div>
        </div>
        {isLoading ? (
          <div style={{ height: 200, background: 'var(--border)', borderRadius: 8, opacity: .25 }} />
        ) : (
          <AreaChartSVG data={activityData} height={200} />
        )}
      </div>

      {/* 3-col bottom */}
      <div className="analytics-bottom">

        {/* Top pages */}
        <div className="card card-pad">
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Most visited pages</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Where people spend their time</div>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1,2,3,4].map(i => <div key={i} style={{ height: 28, background: 'var(--border)', borderRadius: 6, opacity: .3 }} />)}
            </div>
          ) : topPages.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No page visits recorded yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {topPages.map((p, i) => (
                <div key={i} className="mini-bar-row">
                  <span style={{ fontSize: 11, color: 'var(--text-faint)', width: 16 }}>{i + 1}</span>
                  <span className="mini-bar-label">{p.page || '/'}</span>
                  <div className="mini-bar-track">
                    <div className="mini-bar-fill" style={{ width: `${Math.round((p.views / (topPages[0]?.views || 1)) * 100)}%` }} />
                  </div>
                  <span className="mini-bar-count">{p.views.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Traffic sources */}
        <div className="card card-pad">
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Traffic sources</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>How people find you</div>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3].map(i => <div key={i} style={{ height: 24, background: 'var(--border)', borderRadius: 6, opacity: .3 }} />)}
            </div>
          ) : referrerData.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No referrer data yet</div>
          ) : (
            referrerData.map((r, i) => (
              <div key={i} className="source-bar">
                <div className="source-bar-label">
                  <span>{r.source}</span>
                  <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{r.count.toLocaleString()} <span style={{ fontSize: 11 }}>({r.percentage}%)</span></span>
                </div>
                <div className="source-bar-track">
                  <div className="source-bar-fill" style={{ width: `${Math.min(r.percentage, 100)}%`, background: r.color || srcColors[i % srcColors.length] }} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Devices */}
        <div className="card card-pad">
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Devices</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>What visitors use</div>
          {isLoading ? (
            <div style={{ height: 110, background: 'var(--border)', borderRadius: 8, opacity: .25 }} />
          ) : (
            <DonutChart data={deviceTypes.length > 0 ? deviceTypes : [
              { name: 'Mobile',  value: 58, color: '#6c63ff' },
              { name: 'Desktop', value: 36, color: '#5BC0EB' },
              { name: 'Tablet',  value: 6,  color: '#1FB596' },
            ]} />
          )}
        </div>
      </div>

      {/* Hourly traffic + cities */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, alignItems: 'start' }}>

        {/* Hourly bars */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Traffic by hour</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Hour-by-hour pattern</div>
            </div>
          </div>
          {isLoading ? (
            <div style={{ height: 80, background: 'var(--border)', borderRadius: 8, opacity: .25 }} />
          ) : (
            <>
              <div className="hourly-bars">
                {(hourlyData.length > 0 ? hourlyData : Array(24).fill(0)).map((v, i) => {
                  const maxV = Math.max(...hourlyData, 1)
                  return (
                    <div
                      key={i}
                      className="hourly-bar-item"
                      style={{ height: `${Math.max((v / maxV) * 100, 4)}%` }}
                      title={`${i}:00 — ${v} visits`}
                    />
                  )
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'monospace' }}>
                <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
              </div>
            </>
          )}
        </div>

        {/* Top cities */}
        <div className="card card-pad">
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Top locations</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Where visitors are from</div>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3,4,5].map(i => <div key={i} style={{ height: 24, background: 'var(--border)', borderRadius: 6, opacity: .3 }} />)}
            </div>
          ) : (data.locations || []).length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No location data yet</div>
          ) : (
            (data.locations || []).slice(0, 6).map((loc, i) => (
              <div key={i} className="city-row">
                <span style={{ flex: 1, color: 'var(--text)' }}>{loc.city || loc.country || 'Unknown'}</span>
                <div className="mini-bar-track" style={{ width: 50 }}>
                  <div className="mini-bar-fill" style={{ width: `${Math.round((loc.count / ((data.locations[0]?.count) || 1)) * 100)}%` }} />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', width: 44, textAlign: 'right' }}>{loc.count?.toLocaleString()}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default ClientAnalytics
